import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ofType } from '@ngrx/effects';
import { filter, map, Observable, of, switchMap, take, takeUntil, tap, zip } from 'rxjs';

import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
import { AccessToCandidateService } from '@core/access-to-candidate-legacy';
import { AnalyticsMode } from '@core/analytics-types';
import { FullCandidateSelectors } from '@core/candidate-data';
import type { FullCandidate } from '@core/candidate-types';
import { CandidateAnalytics, TrackCandidateTarget } from '@core/candidate-util';
import { Helpers } from '@core/common-legacy';
import { OfferKind } from '@core/offers-types';
import { PlacementActions } from '@core/placements-data';
import type { Placement } from '@core/placements-types';
import { NotificationsController } from '@core/user-interface-legacy';
import { generateUuid } from '@core/utils-legacy';

import { AssignOffersFacade } from './assign-offers.facade';
import type { OfferSelectDialogItem } from '../offer-select/offer-select.types';

@Injectable()
export class AssignOffersCandidateFacade extends AssignOffersFacade<{
  candidate: FullCandidate;
}> {
  private candidateAnalytics = inject(CandidateAnalytics);
  private notifications = inject(NotificationsController);
  private accessToCandidateService = inject(AccessToCandidateService);
  private destroyRef = inject(DestroyRef);
  private abilities = this.store.selectSignal(AbilitiesSelectors.abilityMap);

  public dispatch(): Observable<boolean> {
    this.sendAnalytics();

    return this.checkAndPromptAccessLossConfirmation$().pipe(
      take(1),
      switchMap(confirmed => {
        if (!confirmed) {
          return of(null);
        }

        const observablesArray = this.getObservablesToDispatch();
        return observablesArray.length > 0
          ? zip(...observablesArray).pipe(tap(() => this.handleSuccessToast()))
          : of([]);
      }),
      map(dispatchedObservables => !!dispatchedObservables),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  private checkAndPromptAccessLossConfirmation$(): Observable<boolean> {
    if (this.abilities()[Ability.ViewAllJobs]) {
      return of(true);
    }

    const unassignedPlacementIds = [
      ...this.jobAssignmentsChanges.deleted,
      ...this.talentPoolAssignmentsChanges.deleted,
    ].map(assignment => assignment.placementId!);
    const assignedPlacementIds = [
      ...this.jobAssignmentsChanges.created,
      ...this.talentPoolAssignmentsChanges.created,
    ].map(assignment => assignment.placementId!);

    return this.store
      .select(
        FullCandidateSelectors.currentUserAvailablePlacements({
          candidateId: this.options.candidate.id,
        }),
      )
      .pipe(
        switchMap(availablePlacements => {
          const remainingAvailablePlacements = availablePlacements.filter(
            availablePlacement => !unassignedPlacementIds.includes(availablePlacement.id),
          );
          const didUnassignInitialPlacements = [
            ...this.jobInitialAssignments.map(assignment => assignment.placementId!),
            ...this.talentPoolInitialAssignments.map(assignment => assignment.placementId!),
          ]
            .filter(initialPlacementId =>
              availablePlacements.some(placement => placement.id === initialPlacementId),
            )
            .every(placementId => unassignedPlacementIds.includes(placementId));
          const didUnassignEveryPlacement =
            didUnassignInitialPlacements && !assignedPlacementIds.length;

          if (!remainingAvailablePlacements.length && didUnassignEveryPlacement) {
            return this.accessToCandidateService.promptAccessLossConfirmation$(
              availablePlacements.map(placement => placement.id),
            );
          }

          return of(true);
        }),
      );
  }

  private getObservablesToDispatch(): Array<Observable<Placement>> {
    const jobObservables = this.getJobObservables();
    const talentPoolObservables = this.getTalentPoolObservables();

    return [...jobObservables, ...talentPoolObservables];
  }

  private sendAnalytics(): void {
    if (
      this.jobAssignmentsChanges.created.length ||
      this.talentPoolAssignmentsChanges.created.length
    ) {
      this.candidateAnalytics.trackAssign(
        AnalyticsMode.Single,
        this.getAnalyticsTarget(this.offers, [
          ...this.jobAssignmentsChanges.created.map(assignment => assignment.id),
          ...this.talentPoolAssignmentsChanges.created.map(assignment => assignment.id),
        ]),
      );
    }

    if (
      this.jobAssignmentsChanges.deleted.length ||
      this.talentPoolAssignmentsChanges.deleted.length
    ) {
      this.candidateAnalytics.trackRemove(
        AnalyticsMode.Single,
        this.getAnalyticsTarget(this.offers, [
          ...this.jobAssignmentsChanges.deleted.map(assignment => assignment.id),
          ...this.talentPoolAssignmentsChanges.deleted.map(assignment => assignment.id),
        ]),
      );
    }
  }

  private getAnalyticsTarget(offers: OfferSelectDialogItem[], changes: number[]) {
    const filteredOffers = offers.filter(offer => changes.includes(offer.id));
    const assignmentKind = [
      filteredOffers.some(offer => offer.kind === OfferKind.Job),
      filteredOffers.some(offer => offer.kind === OfferKind.TalentPool),
    ];
    return [TrackCandidateTarget.JOB, TrackCandidateTarget.TALENT_POOL].filter(
      (_, i) => assignmentKind[i],
    );
  }

  private handleSuccessToast() {
    if (
      !this.jobAssignmentsChanges.created.length &&
      !this.talentPoolAssignmentsChanges.created.length
    )
      return;
    let title = '';
    let offerLabel = '';

    const jobs = this.i18n.translate('app.plurals.job', {
      count: this.jobAssignmentsChanges.created.length,
    });
    const talentPools = this.i18n.translate('app.plurals.talent_pool', {
      count: this.talentPoolAssignmentsChanges.created.length,
    });
    const and = this.i18n.translate('app.common.and');

    if (
      this.jobAssignmentsChanges.created.length &&
      this.talentPoolAssignmentsChanges.created.length
    ) {
      offerLabel = `${Helpers.capitalize(jobs)} ${and} ${Helpers.capitalize(talentPools)}`;
      title = this.i18n.translate('app.assignments.editor.offer_assigned', {
        offerLabel,
      });
    } else if (this.jobAssignmentsChanges.created.length) {
      offerLabel = Helpers.capitalize(jobs);
      title = this.i18n.translate('app.assignments.editor.offer_assigned', {
        offerLabel,
      });
    } else if (this.talentPoolAssignmentsChanges.created.length) {
      offerLabel = Helpers.capitalize(talentPools);
      title = this.i18n.translate('app.assignments.editor.offer_assigned', {
        offerLabel,
      });
    }

    this.notifications.success({
      title,
    });
  }

  private getJobObservables(): Array<Observable<Placement>> {
    const observablesArray: Array<Observable<Placement>> = [];
    this.jobAssignmentsChanges.created.forEach(({ id }) => {
      const correlationId = generateUuid();
      this.store.dispatch(
        PlacementActions.createOnePessimistic({
          payload: {
            offerId: id,
            candidateId: this.options.candidate.id,
            correlationId,
            locationIds: this.selectedLocationsByOffer.get(id),
          },
        }),
      );
      const observable = this.actions$.pipe(
        ofType(PlacementActions.createOnePessimisticSuccess),
        filter(({ payload }) => correlationId === payload.correlationId),
        map(({ payload }) => payload.data),
        take(1),
        takeUntil(this.ngOnDestroy$),
      );
      observablesArray.push(observable);
    });

    this.jobAssignmentsChanges.deleted.forEach(({ id }) => {
      const placementId = this.getPlacementIdByOfferId(id).id;
      this.store.dispatch(
        PlacementActions.removeOnePessimistic({
          payload: { data: placementId },
        }),
      );
      const observable = this.actions$.pipe(
        ofType(PlacementActions.removeOnePessimisticSuccess),
        filter(({ payload }) => placementId === payload.data.id),
        map(({ payload }) => payload.data),
        take(1),
        takeUntil(this.ngOnDestroy$),
      );
      observablesArray.push(observable);
    });

    return observablesArray;
  }

  private getTalentPoolObservables(): Array<Observable<Placement>> {
    const observablesArray: Array<Observable<Placement>> = [];
    this.talentPoolAssignmentsChanges.created.forEach(({ id }) => {
      const correlationId = generateUuid();
      this.store.dispatch(
        PlacementActions.createOnePessimistic({
          payload: {
            talentPoolId: id,
            candidateId: this.options.candidate.id,
            correlationId,
          },
        }),
      );
      const observable = this.actions$.pipe(
        ofType(PlacementActions.createOnePessimisticSuccess),
        filter(({ payload }) => correlationId === payload.correlationId),
        map(({ payload }) => payload.data),
        take(1),
        takeUntil(this.ngOnDestroy$),
      );
      observablesArray.push(observable);
    });

    this.talentPoolAssignmentsChanges.deleted.forEach(({ id }) => {
      const placementId = this.getPlacementIdByOfferId(id).id;
      this.store.dispatch(
        PlacementActions.removeOnePessimistic({
          payload: { data: placementId },
        }),
      );
      const observable = this.actions$.pipe(
        ofType(PlacementActions.removeOnePessimisticSuccess),
        filter(({ payload }) => placementId === payload.data.id),
        map(({ payload }) => payload.data),
        take(1),
        takeUntil(this.ngOnDestroy$),
      );
      observablesArray.push(observable);
    });

    return observablesArray;
  }

  private getPlacementIdByOfferId(offerId: number): Placement {
    return this.options.candidate.placements.find(
      placement => offerId === placement.offerId || offerId === placement.talentPoolId,
    )!;
  }
}
